import * as L from 'leaflet';
import clone from 'lodash/clone';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
/**
 * The M.A.A.P. layer management.
 * {@link leaflet#L.Evented}
 */
export class ManagementLayer extends L.Evented {
    /**
     * Create a new layer management instance.
     * @param widget - The M.A.A.P. instance.
     * @param options - The layer management options.
     */
    constructor(widget, options) {
        super();
        /**
         * The layer management options.
         */
        this.options = {};
        /**
         * The available layers.
         */
        this._layers = {};
        this.widget = widget;
        this.options = Object.assign(Object.assign({}, this.options), options);
        this.addEventParent(widget);
    }
    /**
     * Add a layer.
     * @fires LAYERS_LAYER_ADDED
     * @param layer - The layer to add.
     */
    addLayer(layer) {
        if (this._layers[layer.options.name] === undefined) {
            if (!layer.options.group) {
                layer.options.group = 'default';
            }
            layer.addEventParent(this);
            this._layers[layer.options.name] = layer;
            if (layer.options.visible) {
                this.showLayer(layer.options.name);
            }
            this.fire('LAYERS_LAYER_ADDED', { layer }, true);
        }
    }
    /**
     * Remove a layer.
     * @fires LAYERS_LAYER_REMOVED
     * @param name - The name of the layer to remove.
     */
    removeLayer(name) {
        const layer = this._layers[name];
        if (layer) {
            this.hideLayer(name);
            delete this._layers[name];
            this.fire('LAYERS_LAYER_REMOVED', { layer }, true);
        }
    }
    /**
     * Show a layer on the map.
     * @fires LAYERS_LAYER_SHOWN
     * @param name - The name of the  layer to show.
     */
    showLayer(name) {
        const layer = this._layers[name];
        // eslint-disable-next-line
        // @ts-ignore: Property is protected
        if (layer && !layer._map) {
            this.widget.map.addLayer(layer);
            this.fire('LAYERS_LAYER_SHOWN', { layer }, true);
            if (layer.options.includedLayers) {
                layer.options.includedLayers.forEach((included) => {
                    this.showLayer(included);
                });
            }
            if (layer.options.excludedLayers) {
                layer.options.excludedLayers.forEach((excluded) => {
                    this.hideLayer(excluded);
                });
            }
        }
    }
    /**
     * Hide a layer.
     * @fires LAYERS_LAYER_HIDDEN
     * @param name - The name of the layer to hide.
     */
    hideLayer(name) {
        const layer = this._layers[name];
        // eslint-disable-next-line
        // @ts-ignore: Property is protected
        if (layer && layer._map) {
            this.widget.map.removeLayer(layer);
            this.fire('LAYERS_LAYER_HIDDEN', { layer }, true);
        }
    }
    /**
     * Toggle a layer.
     * @param name - The name of the layer to toggle.
     */
    toggleLayer(name) {
        const layer = this._layers[name];
        // eslint-disable-next-line
        // @ts-ignore: Property is protected
        if (layer && layer._map) {
            this.hideLayer(name);
        }
        else {
            this.showLayer(name);
        }
    }
    /**
     * Get a layer.
     * @param name - The name of the layer to get.
     * @returns The requested layer.
     */
    getLayer(name) {
        return this._layers[name];
    }
    /**
     * Get all layers.
     * @returns All layers.
     */
    getAllLayers() {
        return clone(this._layers);
    }
    /**
     * Get all visible layers.
     * @returns All visible layers.
     */
    getVisibleLayers() {
        // eslint-disable-next-line
        // @ts-ignore: Property is protected
        return filter(this._layers, (layer) => !!layer._map);
    }
    /**
     * Get all layer groups.
     * @returns The layer groups.
     */
    getGroups() {
        return uniq(map(this._layers, (layer) => layer.options.group).filter((group) => group !== undefined));
    }
    /**
     * Get all layers for the given group name.
     * @param name - The group name.
     * @returns The group's layers.
     */
    getLayersByGroup(name) {
        return filter(this._layers, (layer) => layer.options.group === name);
    }
    /**
     * Removes all layers from the map and widget and removes the event parent.
     */
    destroy() {
        forEach(this._layers, (layer) => {
            this.removeLayer(layer.options.name);
        });
        this.removeEventParent(this.widget);
    }
}
/**
 * @event LAYERS_LAYER_ADDED
 * @property {external:L.Layer} layer
 */
/**
 * @event LAYERS_LAYER_REMOVED
 * @property {external:L.Layer} layer
 */
/**
 * @event LAYERS_LAYER_SHOWN
 * @property {external:L.Layer} layer
 */
/**
 * @event LAYERS_LAYER_HIDDEN
 * @property {external:L.Layer} layer
 */
//# sourceMappingURL=ManagementLayer.js.map